import { Maybe, RosterEntry, Some } from '../../common/data.js';
import {
	asMutable,
	cloneAsMutable,
	ensure,
	findMatch,
	generateId,
	hasOwnProperty,
	isIn,
	isNone,
	lengthEq,
	makeIndex,
	maybe,
	num,
	replaceMatch,
	updateMatch,
} from '../../common/util.js';
import {
	colorIdVersion,
	ColorTriple,
	ColorTuple,
	findThreadColor,
	hclSimilarity,
	isCommandBasedGraphics,
	mainColorGroups,
	parseRGB,
	profileNameToId,
} from '../../common/data/colors.js';
import {
	convertColors,
	getCommandsForUpload,
	getPreviousColorMappingForUpload,
	getProductTemplate,
	getUpload,
} from './api.js';
import { defaultRosterEntry, getDesignRosterFields, initRosterEntry } from '../../common/data/roster.js';
import { showAlert, showConfirm } from '../components/popup.js';
import chroma from 'chroma-js';
import { DomainId } from '../../common/data/domains.js';
import {
	applyRosterEntry,
	DesignPart,
	DesignParts,
	ProductTemplate,
	ProductTemplateInventory,
	rosterPartTypes,
	TextPartType,
	textPartTypes,
	ThreadColor,
	Upload,
} from '../../common/data/designs.js';
import { freeze } from 'immer';
import { getRosterSizeSort, Point, PublicProduct, WithFeatures } from '../../common/data/products.js';
import { PublicFlow } from '../../common/data/flows.js';

interface DesignManagerState {
	parts: DesignParts;
	partsSorted: DesignPart[];
	roster: RosterEntry[];
	productTemplate?: Maybe<ProductTemplate>;
	productTemplateInventory?: Maybe<ProductTemplateInventory>;
	previewingRoster?: Maybe<string>;
	isRosterFixedSized?: boolean;
}

export type DesignManagerListener = (state: DesignManagerState) => void;

const sortParts = (parts: DesignParts) => {
	const arr = Object.values(parts);
	arr.sort((a, b) => ((a.sort || 0) - (b.sort || 0)) || a.id.localeCompare(b.id) || 0);
	return arr;
};

const defaultTextColors = {
	fill: [255, 255, 255] as ColorTuple,
	stroke: [0, 0, 0] as ColorTuple,
};

export class DesignManager {

	state: DesignManagerState;
	history: DesignManagerState[] = [];
	listeners: DesignManagerListener[] = [];
	domain: DomainId;
	product: PublicProduct & WithFeatures;
	flow: PublicFlow;
	threadColors: Maybe<ThreadColor[]>;
	logoInDesign?: Maybe<boolean>;
	productTemplateInventory?: Maybe<ProductTemplateInventory[]>;
	
	static sortParts = sortParts;
	
	calculateLogoColors?: (part: DesignPart) => Promise<Maybe<ColorTuple[]>>;
	
	constructor({ product, flow, domain, logoInDesign, threadColors, state, productTemplates, productTemplateInventory, skipUpgrade }: {
		product: PublicProduct & WithFeatures;
		flow: PublicFlow;
		domain: DomainId;
		logoInDesign: Maybe<boolean>;
		threadColors?: Maybe<ThreadColor[]>;
		state: Partial<DesignManagerState>;
		productTemplates?: Maybe<ProductTemplate[]>; // only used in constructor, define/assign if ever needed
		productTemplateInventory?: Maybe<ProductTemplateInventory[]>;
		skipUpgrade?: boolean;
	}) {
		
		this.product = product;
		this.flow = flow;
		this.domain = domain;
		this.logoInDesign = logoInDesign;
		this.threadColors = threadColors;
		this.productTemplateInventory = productTemplateInventory;
		
		const parts = maybe(state.parts, parts => skipUpgrade ? parts : this.maybeUpgradeParts(parts, state.productTemplate)) ?? this.makeInitParts();
		
		this.state = {
			roster: [],
			...state,
			parts: freeze(parts, true),
			partsSorted: sortParts(parts),
		};
		
		const app = this.getApparelPart();
		if (!app.productTemplate && lengthEq(productTemplates, 1)) {
			this.setProductTemplate(productTemplates[0]);
		}
		else if (state.productTemplate && app.productTemplate?.id != state.productTemplate.id) {
			this.setProductTemplate(state.productTemplate);
		}
		
		maybe(state.previewingRoster, id => this.applyRosterEntry(this.getRosterEntry()));
		
		if (!skipUpgrade) {
			this.state = {
				...this.state,
				isRosterFixedSized: !rosterPartTypes.find(p => getDesignRosterFields(product, parts, undefined, 'allowed')[p]),
			};
			if (this.state.isRosterFixedSized) {	// name, alias, flag, number are all false -> set roster with all the sizes and qty = 0
			
				const sizes = ensure( this.state.productTemplateInventory?.productSizesFromVendor || product.features?.sizes).map(size => ({
					size,
					sort: getRosterSizeSort(size),
					inStock: this.state.productTemplateInventory?.productSizesInStock ? this.state.productTemplateInventory.productSizesInStock.includes(size) : true,
				}));
				sizes.sort((a, b) => a.sort - b.sort);
	
				for (const sizeObj of sizes) {
					const ex = this.state.roster.filter(e => e.size === sizeObj.size);
					if (ex.length > 1) {
						let add = 0;
						for (const entry of ex) {
							add += num(entry.quantity || 0);
							this.deleteRosterEntry(entry.id);
						}
						if (add) {
							const agg = ex[0]!;
							agg.quantity = num(agg.quantity || 0) + add;
							this.updateRosterEntry(agg.id, { quantity: agg.quantity });
						}
					}
				}
				for (const re of this.state.roster.filter(re => !re.size || !sizes.some(s => s.size == re.size))) {
					this.deleteRosterEntry(re.id);
				}
				
			}
		}
	}
	
	update(changes: Partial<DesignManagerState>) {
		if (changes.parts) {
			changes.partsSorted = sortParts(changes.parts);
			freeze(changes.parts, true);
		}
		this.state = { ...this.state, ...changes };
		this.state.productTemplate = this.getApparelPart().productTemplate;
		if (this.state.productTemplate) {
			this.state.productTemplateInventory = this.productTemplateInventory?.find(pti => pti.id === this.state.productTemplate?.id);
		}
		this.scheduleListeners();
	}
	
	scheduled = false;
	scheduleListeners() {
		if (this.scheduled) return;
		this.scheduled = true;
		setTimeout(() => {
			this.scheduled = false;
			this.listeners.forEach(func => func(this.state));
		}, 0);
	}
	
	addListener(listener: DesignManagerListener) {
		this.listeners = [...this.listeners, listener];
	}
	
	makeInitParts(productTemplate?: Maybe<ProductTemplate>): DesignParts {
		const { features } = this.product;
		const arr = [
			<DesignPart> {
				id: generateId(),
				type: 'apparel',
				colors: {},
				productTemplate: productTemplate,
				productRevision: features?.revision,
				sort: 0,
			},
			features?.flag && this.makeInitFlag(),
			features?.pwLogo && ensure(this.logoInDesign) && this.makeInitPWLogo(),
		].filter(p => !!p);
		return makeIndex(arr);
	}
	
	makeInitFlag(): DesignPart {
		return {
			id: generateId(),
			sort: 10,
			type: 'flag',
			colors: {
				outline: [255, 255, 255],
			},
			flag: {
				country: undefined,
				outline: false,
				left: 'off',
				right: 'off',
			},
			position: 'flag',
		};
	}
	
	makeInitPWLogo(product = this.product): DesignPart {
		return {
			id: generateId(),
			sort: 20,
			type: 'pw-logo',
			domain: this.domain,
			colors: {
				logo: [255, 255, 255],
			},
			position: product.features?.defaultPWPosition || 'pw-front',
		};
	}
	
	maybeUpgradeParts(parts: DesignParts, productTemplate?: Maybe<ProductTemplate>) {
		
		const { features } = this.product;
		const profile = this.flow.color_profile || 'rgb';
		const profileId = profileNameToId[profile];
		const list = Object.values(parts) as DesignPart[]; // make mutable
		
		const apparel = ensure(findMatch(list, { type: 'apparel' }));
		if (apparel.productDesign) {
			apparel.productTemplate = apparel.productDesign;
			delete apparel.productDesign;
		}
		
		if (this.product.id == 'gamepadxl' && (apparel.productRevision || 0) < 2) {
			for (const part of list) {
				const { point } = part;
				if (!point) continue;
				for (const k of ['x', 'y', 'w', 'h'] as const) if (point[k]) point[k] *= 3.631;
				if (point.y) point.y -= 0.09;
			}
			ensure(apparel).productRevision = 2;
		}
		
		const fixUploadColors = async (part: DesignPart) => {
			if (!part.upload?.colors?.length || profile.match(/^s?rgb$/)) return;
			const res = await convertColors({ colors: part.upload.colors, profile });
			if (!res.colors) return;
			this.updatePart(part.id, {
				upload: {
					...part.upload,
					colors: res.colors,
				},
			});
		};
		
		const fixableColors: [string, string, number[]][] = [];
		
		for (const [_xpi, part] of list.entries()) {
		
			const leg = part as any;
			
			// noinspection SuspiciousTypeOfGuard
			if (typeof part.id != 'string') part.id = generateId();
			const pi = part.id;
			
			if (part.productTemplate) {
				part.productTemplate = productTemplate;
			}
			if (!part.point && (leg.x || leg.w || leg.side)) {
				part.point = {
					x: leg.x,
					y: leg.y,
					w: leg.w,
					h: leg.h,
					side: leg.side,
				};
			}
			if (!part.position) {
				part.position = ({
					number: 'number',
					name: 'name',
					alias: 'alias',
					'pw-logo': 'pw-front',
					flag: 'flag',
				} as any)[part.type];
			}
			if (part.position == 'custom' && part.point && !part.point.area) {
				part.point.area = 'main';
			}
			if (part.point && part.point.area && features?.areas && !features.areas[part.point.area]) {
				part.point.area = Object.keys(features.areas).sort()[0];
			}
			if (part.upload?.colors && part.upload.colors[0] && part.upload.colors[0].length < 12) {
				fixUploadColors(part);
			}
			if (part.colors) for (const cid in part.colors) {
				const color = part.colors[cid];
				if (color && (
					color.length < 12
					|| (color[11] || 0) < colorIdVersion
					|| color[10] != profileId
				)) fixableColors.push([pi, cid, color]);
			}
			
			const { threadColors } = this;
			if (part.threads && threadColors) {
				for (const [cid, tid] of Object.entries(part.threads)) {
					const cur = this.findThreadColor(tid);
					if (!cur) continue;
					(part.colors ||= {})[cid] = cur.rgb;
					part.threads[cid] = cur.id;
				}
			}
			if (threadColors && !part.threads && part.text && isIn(part.type, textPartTypes)) {
				const inputColors: DesignPart['colors'] = { ...defaultTextColors, ...part.colors };
				Object.assign(part, this.matchThreadsByColor(inputColors));
			}
			
			if (part.position && part.position != 'custom') {
				this.assignDefaultPoint(part, productTemplate);
			}
			if (part.flag && !hasOwnProperty(part.flag, 'left') && !hasOwnProperty(part.flag, 'right')) {
				part.flag.left = 'off';
				part.flag.right = 'off';
			}
			if (part.type == 'pw-logo') {
				part.domain = this.domain;
			}
		}
		
		if (features?.flag && !list.find(p => p.type == 'flag')) {
			list.push(this.makeInitFlag());
		}
		
		this.maybeUpgradePwLogo(list, this.product, productTemplate);
		
		if (fixableColors.length && profile != 'rgb') (async () => {
			const res = await convertColors({ colors: fixableColors.map(fcr => fcr[2]), profile });
			if (!res.colors) return;
			for (const [i, [pi, cid, _orig]] of fixableColors.entries()) {
				this.updatePart(pi, { colors: {
					...this.getPart(pi)!.colors,
					[cid]: res.colors[i],
				} });
			}
		})();
		
		return makeIndex(list);
	}
	
	getApparelPart() {
		return this.state.partsSorted.find(p => p.type == 'apparel')!;
	}
	
	findThreadColor(tid: Maybe<string>) {
		const { threadColors } = this;
		if (!threadColors) return null;
		let cur = maybe(tid, tid => findThreadColor(threadColors, tid));
		if (cur?.fallback && cur.fallback != cur.id) cur = findThreadColor(threadColors, cur.fallback);
		return cur;
	}
	
	assignPoint(part: DesignPart, point?: Point) {
		
		if (!point) return;
		
		if (!point.side) point.side = 'front';
		if (!point.area) point.area = 'main';
		
		// Kludge to reset bounding rect to square for TS logo
		if (part.type == 'pw-logo') {
			part.point = {
				...point,
				h: point.w,
			};
			return;
		}
		
		const [aw, ah] = part.upload?.width && part.upload.height ? [part.upload.width, part.upload.height] : [part.point?.w, part.point?.w];
		let { x, y } = point;
		if (!point.adjust || !aw || !ah || !point.w || !point.h || isNone(x) || isNone(y)) {
			part.point = point;
			return;
		}
		
		let w: number, h: number;
		
		if (aw / point.w > ah / point.h) {
			w = point.w!;
			h = point.w * (ah / aw);
		}
		else {
			h = point.h!;
			w = point.h * (aw / ah);
		}
		
		
		if (point.alignY == 'top') y -= (point.h - h) / 2;
		else if (point.alignY == 'bottom') y += (point.h - h) / 2;
		if (point.alignX == 'left') x -= (point.w - w) / 2;
		else if (point.alignX == 'right') x += (point.w - w) / 2;
		
		part.point = { ...point, x, y, w, h };
		
	}
	
	assignDefaultPoint(part: DesignPart, productTemplate: Maybe<ProductTemplate>, product = this.product) {
		
		const points: Record<string, Point | null> = { ...product.features?.points, ...productTemplate?.points };
		
		let point = points[`${part.position || part.type}-${part.point?.area}`];
		if (point) return this.assignPoint(part, point);
		
		if (part.type == 'pw-logo') {
			const pwPoint = points[part.position || part.type];
			if (pwPoint) return this.assignPoint(part, pwPoint);
			const newPWPoint = points[product.features?.defaultPWPosition || 'pw-front'];
			if (newPWPoint) return this.assignPoint(part, newPWPoint);
		}
		
		point = points[`default-${part.point?.area}`] || (part.position && points[part.position]) || points[part.type];
		if (point) this.assignPoint(part, point);
		
	}
	
	async fetchAndSetProductTemplate(id: Maybe<string>) {
		const pt = !!id && await getProductTemplate({ id }).then(res => ensure(res.productTemplate));
		this.setProductTemplate(pt || null);
	}
	
	setProductTemplate(productTemplate: Maybe<ProductTemplate>) {
		const parts = { ...this.state.parts };
		const app = this.getApparelPart();
		parts[app.id] = { ...app, productTemplate };
		if (productTemplate) this.state.partsSorted.forEach(part => {
			if (!part.position || part.position == 'custom') return;
			const clone = cloneAsMutable(part);
			this.assignDefaultPoint(clone, productTemplate);
			parts[part.id] = clone;
		});
		this.update({ parts, productTemplate });
	}
	
	setPartColor(pid: string, cid: string, value: Maybe<string | ColorTuple>, thread?: Maybe<string>) {
		
		const pval = typeof value == 'string' ? parseRGB(value) : value;
		if (!pval && value !== null) return;
		
		const part = this.getPart(pid);
		if (!part) return;
		
		this.updatePart(part.id, {
			colors: { ...part.colors, [cid]: pval },
			threads: part.threads ? { ...part.threads, [cid]: thread } : thread ? { [cid]: thread } : undefined,
		});
		
	}
	
	setPreset(colors: ColorTuple[]) {
		const app = this.getApparelPart();
		const dest = cloneAsMutable(app.colors ?? {});
		const colorGroups = this.product.features?.colorGroups ?? [];
		mainColorGroups.filter(cid => isIn(cid, colorGroups)).forEach((cid, idx) => dest[cid] = colors[idx]);
		this.updatePart(app.id, { colors: dest });
	}
	
	addPart(part: Omit<DesignPart, 'id' | 'sort'>): Readonly<DesignPart> {
		
		const apart = this.getApparelPart();
		if (part.position) this.assignDefaultPoint(part as DesignPart, apart.productTemplate);
		const npart: DesignPart = {
			...part,
			id: generateId(),
			sort: 10 * (this.state.partsSorted.length + 1),
		};
		const parts = { ...this.state.parts, [npart.id]: npart };
		
		this.update({ parts });
		
		if (part.type == 'logo' && part.upload) this.processLogo(npart);
		
		return freeze(npart);
		
	}
	
	checkRoster = (part: DesignPart) => {
		if (this.state.partsSorted.filter(pt => part.type === pt.type).length === 1) return this.state.roster.some(entry => entry[part.type as keyof RosterEntry]);
	};
	
	async removePart(pid: string) {
		const part = this.state.parts[pid];
		if (!part) return;
		const updated = makeIndex(this.state.partsSorted.filter(p => p.id != pid));
		if (!this.checkRoster(part)) return this.update({ parts: updated });
		const confirm = await showConfirm(`You have ${part.type} set for some of your roster entries. Are you sure you want to remove the last ${part.type} in the design?`);
		if (confirm) this.update({ parts: updated });
	}
	
	getPart(pid: string) {
		return this.state.parts[pid];
	}
	
	updatePart(pid: string, props: Partial<DesignPart>): Maybe<Readonly<DesignPart>> {
		
		const prev = this.getPart(pid);
		if (!prev) return;
		
		const part = asMutable({
			...prev,
			...props,
		});
		
		const apart = this.getApparelPart();
		if (props.position && props.position != 'custom' && apart.productTemplate) {
			this.assignDefaultPoint(part, apart.productTemplate);
		}
		
		if (this.threadColors && part.text && props.threads && !props.text) {
			part.text = { ...part.text };
			for (const key of ['fill', 'stroke'] as const) {
				part.text[key] = !!props.threads[key];
			}
		}
		
		const reid = this.state.previewingRoster;
		const rosterUp: Partial<RosterEntry> = {};
		if (reid && part.type == 'name') rosterUp.name = part.text!.text;
		if (reid && part.type == 'number') rosterUp.number = part.text!.text;
		if (reid && part.type == 'alias') rosterUp.alias = part.text!.text;
		if (reid && part.type == 'flag') rosterUp.flag = part.flag && part.flag.country;
		
		this.update({
			parts: { ...this.state.parts, [part.id]: part },
			roster: reid && Object.keys(rosterUp).length
				? updateMatch(this.state.roster, { id: reid }, rosterUp)
				: this.state.roster,
		});
		
		return freeze(part);
		
	}
	
	movePart(pid: string, dir: number) {
		const part = this.state.parts[pid];
		const sorted = this.state.partsSorted;
		const cur = sorted.findIndex(p => p.id == pid);
		const other = sorted[cur + dir];
		if (!part || !other) return;
		const otherSort = part.sort != other.sort ? other.sort : part.sort + dir;
		this.update({ parts: {
			...this.state.parts,
			[part.id]: { ...part, sort: otherSort },
			[other.id]: { ...other, sort: part.sort },
		} });
	}
	
	addUpload(upload: Upload) {
		return this.addPart({
			type: 'logo',
			upload,
			position: 'logo',
			point: {
				w: upload.width,
				h: upload.height,
				side: 'front',
			},
		});
	}
	
	addTextPart(type: TextPartType = 'text') {
		
		const { previewingRoster, roster } = this.state;
		const text = type == 'text' ? 'New text' : (
			maybe(previewingRoster, id => findMatch(roster, { id })) ?? defaultRosterEntry
		)[type] ?? '';
		
		const { threads, colors } = this.threadColors ? this.matchThreadsByColor(defaultTextColors) : { threads: null, colors: defaultTextColors };
		
		return this.addPart({
			type,
			position: type,
			text: { fill: true, stroke: true, font: 'Impact', text },
			threads,
			colors,
		});
	}
	
	ensureFlag() {
		let flag = this.state.partsSorted.find(p => p.type == 'flag');
		if (!flag) flag = this.addPart(this.makeInitFlag());
		if (flag.flag && (flag.flag.left == 'on' || flag.flag.right == 'on')) return flag;
		return ensure(this.updatePart(flag.id, { flag: {
			...flag.flag,
			left: 'on',
		} }));
	}
	
	getRosterEntry = (id = this.state.previewingRoster) => {
		if (id) return findMatch(this.state.roster, { id });
	};
	
	applyRosterEntry = (entry?: RosterEntry) => {
		const parts = applyRosterEntry(this.state.parts, entry || defaultRosterEntry);
		this.update({ parts });
	};
	
	updateRosterEntry = (id: string, data: Partial<RosterEntry>) => {
		
		const { roster } = this.state;
		
		const found = findMatch(roster, { id });
		const entry = {
			...(found ?? { id }),
			...data,
			quantity: Math.round(Number(data.quantity ?? found?.quantity ?? 0)),
		};
		
		this.applyRosterEntry(entry);
		this.update({
			roster: found ? replaceMatch(roster, { id }, entry) : [...roster, entry],
			previewingRoster: id,
		});
		
	};
	
	deleteRosterEntry = (id: string) => {
		const { roster, previewingRoster } = this.state;
		const saw = previewingRoster == id;
		const filtered = roster.filter(re => re.id != id);
		
		this.update({
			roster: filtered.length || this.state.isRosterFixedSized ? filtered : [initRosterEntry()],
			previewingRoster: saw ? undefined : previewingRoster,
		});
		if (saw) this.applyRosterEntry();
		
	};
	
	previewRosterEntry = (id: Maybe<string>, toggle = true) => {
		
		if (toggle && this.state.previewingRoster == id) {
			this.update({ previewingRoster: undefined });
			this.applyRosterEntry();
			return;
		}
		
		const entry = id && this.getRosterEntry(id) || undefined;
		this.applyRosterEntry(entry);
		
		this.update({ previewingRoster: id });
		
	};
	
	replaceRoster = (roster: RosterEntry[]) => {
		const first = roster[0];
		if (first) this.applyRosterEntry(first);
		this.update({
			roster,
			previewingRoster: first?.id,
		});
	};
	
	reset() {
		const parts = this.makeInitParts();
		for (const part of Object.values(parts)) {
			if (part.position) this.assignDefaultPoint(part, null);
		}
		this.update({
			parts: parts,
			productTemplate: undefined,
			roster: this.state.isRosterFixedSized ? [] : [initRosterEntry()],
			previewingRoster: undefined,
		});
	}
	
	exportParts({ parts = this.state.parts, lockColors = true }: { parts?: DesignParts; lockColors?: boolean } = {}): DesignParts {
		return makeIndex(Object.values(applyRosterEntry(parts)).map(part => ({
			...part,
			productTemplate: part.productTemplate && { id: part.productTemplate.id } as ProductTemplate, // FIXME typing
			colors: part.colors || part.productTemplate ? {
				...(lockColors ? part.productTemplate?.template_info?.colors : {}),
				...part.colors,
			} : undefined,
		})));
	}
	
	async processAllLogos() {
		for (const part of Object.values(this.state.parts)) {
			if (part.type != 'logo') continue;
			await this.processLogo(part);
		}
	}
	
	async matchColors(part: DesignPart) {
	
		const { threadColors } = this;
		if (!threadColors) return;
		
		const upload = ensure(part.upload);
		let curThreads = part.threads;
		
		const res = await getCommandsForUpload({ upload: upload.id }).catch(async e => {
			await showAlert(
				'There was an error when processing the logo for embroidery. Please refresh the page or contact support if the error persists. Sorry for the inconvenience.'
			);
			throw new Error('processLogo failed for embroidery', { cause: e });
		});
		
		const prev = await getPreviousColorMappingForUpload({
			upload: upload.id,
			decoration: ensure(this.flow.decoration),
		});
		curThreads = { ...prev.threads, ...curThreads };
		
		const palette = ensure(res.upload.commands_palette).map((cpe, i) => ({ cid: String(i + 1), rgb: cpe.slice(0, 3) as ColorTriple }));
		const { threads, colors } = this.matchThreadsByColor(Object.fromEntries(palette.map(({ cid, rgb }) => [cid, rgb] as const)));
		for (const { cid } of palette) {
			
			const tid = curThreads[cid];
			if (tid === null) {
				colors[cid] = null;
				threads[cid] = null;
				continue;
			}
			
			const cur = this.findThreadColor(tid);
			if (cur) {
				colors[cid] = cur.rgb;
				threads[cid] = cur.id;
			}
			
		}
		
		this.updatePart(part.id, {
			vector_upload: res.upload,
			colors,
			threads,
		});
		
	}
	
	matchThreadsByColor(inputColors: Some<DesignPart['colors']>) {
		
		const currentColors = ensure(this.threadColors).filter(c => isNone(c.fallback));
		
		const hcls = currentColors.map(ec => chroma.rgb(ec.rgb[0], ec.rgb[1], ec.rgb[2]).hcl());
		const colors: DesignPart['colors'] = {};
		const threads: DesignPart['threads'] = {};
		
		for (const [cid, rgb] of Object.entries(inputColors)) {
		
			if (!rgb) {
				colors[cid] = null;
				threads[cid] = null;
				continue;
			}
			
			const dest = chroma.rgb(rgb[0], rgb[1], rgb[2]);
			const dhcl = dest.hcl();
			const options = currentColors.map((cand, j) => {
				const chcl = hcls[j]!;
				return [
					hclSimilarity(chcl, dhcl),
					cand,
				] as const;
			});
			
			options.sort((a, b) => a[0] - b[0]);
			const best = options[0]![1];
			colors[cid] = best.rgb;
			threads[cid] = best.id;
			
		}
		
		return { threads, colors };
		
	}
	
	async processLogo(part: DesignPart) {
		
		if (!part.upload) return;
		
		const { graphics } = this.flow;
		
		if (graphics == 'digitize' || graphics == 'screen-print') await this.matchColors(part);
		
		if (!isCommandBasedGraphics(this.flow) && !part.upload.colors && this.calculateLogoColors) {
			const colors = await this.calculateLogoColors(part);
			if (colors?.length) {
				const apparel = this.getApparelPart();
				if (!apparel.colors || !Object.keys(apparel.colors).length) {
					const [a, b] = colors;
					this.updatePart(apparel.id, {
						colors: b ? {
							base: [0, 0, 0],
							primary: a,
							secondary: b,
							accent: b,
							collar: [255, 255, 255],
						} : {
							base: [0, 0, 0],
							primary: [0, 0, 0],
							secondary: a,
							accent: [255, 255, 255],
							collar: a,
						},
					});
				}
			}
		}
		
	}
	
	async fetchAndAddUpload(ulid: string) {
		const { upload } = await getUpload({ upload: ulid });
		if (upload) this.addUpload(upload);
	}
	
	adaptPartsToProduct(parts: DesignParts, product: PublicProduct & WithFeatures, productTemplate: ProductTemplate) {
	
		const { features } = product;
		
		let op = Object.values(parts).map(np => {
			const p = { ...np } as DesignPart;
			if (p.type == 'apparel') {
				p.productTemplate = productTemplate;
				p.productRevision ||= product.features?.revision;
			}
			if (p.position) {
				const { w, h } = p.point || {};
				if (
					p.position != 'custom'
					|| (!p.point || !w || !h || !p.point.area)
					|| !features?.areas?.[p.point.area]
				) {
					this.assignDefaultPoint(p, productTemplate, product);
				}
				else if (features.maxLogoSize) {
					const [mw, mh] = features.maxLogoSize;
					if (w > mw && h > mh) {
						if (w / mw >= h / mh) p.point = { ...p.point, w: mw, h: h * (mw / w) };
						else p.point = { ...p.point, h: mh, w: w * (mh / h) };
					}
				}
			}
			return p;
		});
		
		const haveFlag = op.some(p => p.type == 'flag');
		const removeFlag = !features?.flag;
		if (!removeFlag && !haveFlag) op.push(this.makeInitFlag());
		else if (removeFlag && haveFlag) op = op.filter(p => p.type != 'flag');
		
		const haveRoster = op.some(p => p.type == 'name' || p.type == 'number' || p.type == 'alias');
		if ((!features?.roster || !features.nameNumber) && haveRoster) op = op.filter(p => p.type != 'name' && p.type != 'number' && p.type !== 'alias');
		
		this.maybeUpgradePwLogo(op, product, productTemplate);
		
		return makeIndex(op);
		
	}
	
	maybeUpgradePwLogo(parts: DesignPart[], product: PublicProduct & WithFeatures, productTemplate: Maybe<ProductTemplate>) {
		const { features } = product;
		const pwPart = parts.find(p => p.type == 'pw-logo');
		const removePW = !(features?.pwLogo && ensure(this.logoInDesign));
		if (!removePW && !pwPart) {
			const p = this.makeInitPWLogo(product);
			this.assignDefaultPoint(p, productTemplate, product);
			parts.push(p);
		}
		else if (removePW && pwPart) {
			parts.splice(parts.indexOf(pwPart), 1);
		}
		else if (pwPart && !productTemplate?.points?.[pwPart.position!] && !features?.points?.[pwPart.position!]) {
			this.assignDefaultPoint(pwPart, productTemplate, product);
		}
	}
	
}
